import * as React from "react"
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react"
import { DayButton, DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-white border border-slate-200 rounded-xl shadow-lg p-4 w-[280px] [--cell-size:2.25rem]",
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: "w-[280px]",
        months: "relative flex flex-col gap-4",
        month: "flex w-full flex-col gap-3",
        nav: "absolute inset-x-0 top-0 flex w-full items-center justify-between px-1 h-9 pointer-events-none",
        button_previous: "h-7 w-7 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-all cursor-pointer pointer-events-auto shadow-sm",
        button_next: "h-7 w-7 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-all cursor-pointer pointer-events-auto shadow-sm",
        month_caption: "flex h-9 w-full items-center justify-center text-sm font-bold text-slate-800",
        dropdowns: "flex h-9 w-full items-center justify-center gap-1.5 text-sm font-medium",
        dropdown_root: "relative rounded-md border border-slate-200 bg-white shadow-sm",
        dropdown: "absolute inset-0 opacity-0",
        caption_label: "select-none font-bold text-sm text-slate-800",
        table: "w-full border-collapse",
        weekdays: "flex w-full justify-between border-b border-slate-100 pb-1.5",
        weekday: "text-slate-400 select-none text-[10px] font-bold uppercase tracking-wider w-[--cell-size] h-8 flex items-center justify-center",
        week: "mt-1.5 flex w-full justify-between",
        week_number_header: "w-[--cell-size] select-none text-[10px] font-bold text-slate-400",
        week_number: "text-slate-400 select-none text-[0.8rem] w-[--cell-size] h-[--cell-size] flex items-center justify-center",
        day: "group/day relative select-none p-0 text-center w-[--cell-size] h-[--cell-size] flex items-center justify-center",
        range_start: "bg-blue-50 rounded-l-md",
        range_middle: "bg-blue-50/50",
        range_end: "bg-blue-50 rounded-r-md",
        today: "font-bold text-blue-600 bg-blue-50 rounded-full",
        outside: "text-slate-300 pointer-events-none opacity-40",
        disabled: "text-slate-200 opacity-50 pointer-events-none",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            )
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  const isSelected = modifiers.selected;
  const isToday = modifiers.today;

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      className={cn(
        "h-8 w-8 rounded-full text-xs transition-all flex items-center justify-center cursor-pointer",
        isSelected
          ? "bg-[#0f3453] text-white hover:bg-[#0f3453] font-bold shadow-sm"
          : isToday
            ? "bg-blue-50 text-blue-600 font-bold hover:bg-blue-100"
            : "text-slate-700 hover:bg-slate-100",
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
